// @wc-ignore-file
/**
 * CR-163 — the one rule for "is this link inside the app?".
 *
 * A link (from an AI message, a doc-list widget, a document chip…) is:
 * - `internal` when it resolves to this origin: hand `path` (app-relative,
 *   search and hash kept) to the router. `//host` and `/\host` are NOT
 *   internal — browsers read both as protocol-relative urls to another host,
 *   and so does the standard URL parser used here (tabs and newlines stripped too).
 * - `external` when it is an http(s) url on another origin: render a new-tab
 *   anchor with `rel="noopener noreferrer"` on the normalised absolute `href`.
 * - `text` for everything else (empty, invalid, bare `example.com`,
 *   `javascript:`, `data:`, `mailto:`, `tel:`, `ftp:`…): render no link. That
 *   includes a same-origin url whose dot segments normalise to `//host/…`
 *   (`/..//host`): the router would read that path as protocol-relative.
 *
 * Pure and React/DOM-free apart from the `window.location.origin` default
 * (without a window and no explicit origin, every href is `text`).
 */

export type AppLink =
  | { kind: "internal"; path: string }
  | { kind: "external"; href: string }
  | { kind: "text" };

const TEXT: AppLink = { kind: "text" };

function currentOrigin(): string {
  return typeof window === "undefined" ? "" : window.location.origin;
}

export function classifyLink(
  href: string | null | undefined,
  origin: string = currentOrigin(),
): AppLink {
  if (!href) {
    return TEXT;
  }
  let base: URL;
  let url: URL;
  try {
    base = new URL(origin);
    // A leading `/` is resolved against the app origin (so `//host` and
    // `/\host` land on `host`); anything else must be an absolute url.
    url = href.startsWith("/") ? new URL(href, base) : new URL(href);
  } catch {
    return TEXT;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") {
    return TEXT;
  }
  if (url.origin === base.origin) {
    // Dot segments can normalise to `//host/…` (`/..//host`, `/.//host`): as an
    // app-relative path that is protocol-relative again, so it is no link.
    if (url.pathname.startsWith("//")) {
      return TEXT;
    }
    return { kind: "internal", path: `${url.pathname}${url.search}${url.hash}` };
  }
  // The normalised url, so the DOM shows where the link really goes
  // (`//host` and `/\\host` read as app paths otherwise).
  return { kind: "external", href: url.href };
}
